import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import { LockMid } from '@zooplus/icons';
import { Checkbox } from '@zooplus/ui-components';

import { cardTypeOfCreditCardBrand, CreditCardBrand, Nullable } from '../../../types';
import { exposeGlobally } from '../../../utils/utils';
import { useAppContext } from '../../AppContextProvider';
import CardIcon from '../../CardIcon/CardIcon';
import FormControl from '../../FormControl/FormControl';
import FormControlWrapper from '../../FormControl/FormControlWrapper';
import { AdyenConfig } from '../Adyen.types';

import styles from './AdyenCreditCardForm.module.scss';
import { RENDER_CONTAINER_ID } from './AdyenCreditCardForm.static';
import useAdyenSecureFields from './hooks/useAdyenSecureFields';
import useAdyenState from './hooks/useAdyenState';
import '@adyen/adyen-web/dist/adyen.css';
import { AdyenCreditCardFormData } from './hooks/useAdyenState.static';

export type AdyenCreditCardFormRef = {
  computeAdyenCreditCardFormData: () => Nullable<AdyenCreditCardFormData>;
};

export interface AdyenCreditCardFormProps extends AdyenConfig {
  supportedCreditCardBrands: CreditCardBrand[];
  allowVaulting: boolean;
  labelInline: boolean;
  onFatalError: (error: unknown, hideForm?: boolean) => void;
}

/**
 * The UI to add a new credit card, the security is handled by Adyen.
 * Relies on the hook 'useAdyenState' (to capture the data from Adyen and the unmanaged inputs) and 'useAdyenSecureFields' (to initialize the Adyen 'securedfields').
 *
 * Captured data can be retrieved with the globally-exposed 'computeAdyenCreditCardFormData'.
 */
const AdyenCreditCardForm = forwardRef<AdyenCreditCardFormRef, AdyenCreditCardFormProps>(
  (
    {
      adyenEnvironment,
      supportedCreditCardBrands,
      clientKey,
      allowVaulting,
      labelInline,
      onFatalError,
    },
    forwardedRef,
  ) => {
    const { t } = useTranslation();
    const { locale } = useAppContext();
    const {
      onChange,
      onFieldValid,
      onHolderNameChange,
      storeInVault,
      onStoreInVaultChange,
      focusedElement,
      onFocus,
      creditCardBrand,
      onBrand,
      computeAdyenCreditCardFormData,
      errors,
    } = useAdyenState(onFatalError);

    useAdyenSecureFields(
      {
        clientKey,
        locale,
        t,
        adyenEnvironment,
        onChange,
        onFocus,
        onFieldValid,
        onBrand,
        onFatalError,
      },
      supportedCreditCardBrands,
    );

    // Expose the captured state globally so that external code can get the Adyen encrypted data
    useEffect(
      () => exposeGlobally('computeAdyenCreditCardFormData', computeAdyenCreditCardFormData),
      [computeAdyenCreditCardFormData],
    );

    // Expose the computeAdyenCreditCardFormData method internally too
    useImperativeHandle(forwardedRef, () => ({
      computeAdyenCreditCardFormData,
    }));

    const isAmex = creditCardBrand === 'american_express';

    return (
      <div id={RENDER_CONTAINER_ID} data-zta="adyen-cc-form-container">
        <div className={styles.activeCards}>
          {supportedCreditCardBrands.map((supportedCreditCardBrand) => (
            <CardIcon
              key={supportedCreditCardBrand}
              type={cardTypeOfCreditCardBrand[supportedCreditCardBrand]}
              className={classNames(
                (creditCardBrand === null || creditCardBrand === supportedCreditCardBrand) &&
                  styles.activeCard,
              )}
            />
          ))}
        </div>
        <FormControlWrapper
          label={t('checkout.payment.creditcard.card_number')}
          labelFor="encryptedCardNumber"
          labelInline={labelInline}
          infoMessage={t('checkout.payment.secure')}
          infoMessageIcon={<LockMid />}
          errorMessage={errors.encryptedCardNumber}
          errorZta="errorInputCreditCardNumber">
          <FormControl
            innerClassname={styles.noPadding}
            id="encryptedCardNumber"
            data-cse="encryptedCardNumber"
            hasError={errors.encryptedCardNumber !== null}
            focused={focusedElement === 'encryptedCardNumber'}
            data-zta="inputCreditCardNumber"
          />
        </FormControlWrapper>

        <FormControlWrapper
          label={t('checkout.payment.creditcard.holder_name')}
          labelFor="holderName"
          labelInline={labelInline}
          errorMessage={errors.holderName}
          errorZta="errorInputCardName">
          <input
            onChange={onHolderNameChange}
            className={classNames(
              'v3-form__input',
              styles.holderName,
              errors.holderName !== null && 'v3-form__input--error',
            )}
            type="text"
            id="holderName"
            data-zta="inputCardName"
          />
        </FormControlWrapper>

        <FormControlWrapper
          label={t('checkout.payment.creditcard.expiry_date')}
          labelFor="encryptedExpiryDate"
          labelInline={labelInline}
          errorMessage={errors.encryptedExpiryDate}
          errorZta="errorInputCardDate">
          <div
            id="encryptedExpiryDate"
            className={classNames(styles.notFullWidth, styles.expiryDateContainer)}>
            <FormControl
              innerClassname={classNames(styles.noPadding)}
              id="encryptedExpiryMonth"
              data-cse="encryptedExpiryMonth"
              hasError={errors.encryptedExpiryDate !== null}
              focused={focusedElement === 'encryptedExpiryMonth'}
              data-zta="inputCardMonth"
            />
            <span>/</span>
            <FormControl
              innerClassname={classNames(styles.noPadding)}
              id="encryptedExpiryYear"
              data-cse="encryptedExpiryYear"
              hasError={errors.encryptedExpiryDate !== null}
              focused={focusedElement === 'encryptedExpiryYear'}
              data-zta="inputCardYear"
            />
          </div>
        </FormControlWrapper>

        <FormControlWrapper
          label={t('checkout.payment.creditcard.cvc.generic_code')}
          labelFor="encryptedSecurityCode"
          labelInline={labelInline}
          infoMessage={
            isAmex
              ? t('checkout.payment.creditcard.cid.warning')
              : t('checkout.payment.creditcard.cvc.warning')
          }
          errorMessage={errors.encryptedSecurityCode}
          errorZta="errorInputCardCode">
          <FormControl
            innerClassname={classNames(
              styles.notFullWidth,
              styles.withIconWithin,
              styles.noPadding,
            )}
            id="encryptedSecurityCode"
            data-cse="encryptedSecurityCode"
            withinImageIconType={isAmex ? 'cvvFront' : 'cvvBack'}
            hasError={errors.encryptedSecurityCode !== null}
            focused={focusedElement === 'encryptedSecurityCode'}
            data-zta="inputCardCode"></FormControl>
        </FormControlWrapper>

        {allowVaulting && (
          <FormControlWrapper labelInline={labelInline}>
            <Checkbox
              className={styles.vaultCheck}
              id="vault-check"
              checked={storeInVault}
              onChange={onStoreInVaultChange}
              data-zta="storeCreditCard">
              {t('checkout.payment.save.creditcard')}
            </Checkbox>
          </FormControlWrapper>
        )}
      </div>
    );
  },
);

export default AdyenCreditCardForm;
